import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Mail, MapPin, MessageCircle } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

const INTERESTS = ["Campaña con influencers", "Producción de contenido", "Streaming / evento en vivo", "Desarrollo de producto", "Representación de talento", "Auspicio de programas", "Otro"];

export default function ContactPage() {
  const { config } = useSite();
  const [form, setForm] = useState({
    name: "", company: "", role: "", email: "", whatsapp: "", city: "",
    interest: INTERESTS[0], message: "", accept_contact: true,
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true); setError("");
    try {
      await api.post("/leads", form);
      setDone(true);
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || "Error al enviar.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page" className="relative">
      <section className="pt-20 pb-12 lg:pt-32 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-xs uppercase tracking-[0.3em] text-primary mb-6 font-bold">Contacto</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl font-black tracking-tighter leading-[0.95] mb-6 max-w-5xl">
            Contanos qué <span className="gradient-text">querés mover</span>.
          </h1>
          <p className="text-base lg:text-lg text-muted-foreground max-w-3xl">
            Nosotros ponemos la estrategia, los talentos y la producción.
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-1 space-y-8">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-3 font-bold">Datos directos</p>
              <ul className="space-y-4 text-sm">
                {config?.email && <li className="flex items-start gap-3"><Mail className="w-4 h-4 mt-1 text-primary" /><a href={`mailto:${config.email}`} className="hover:text-primary">{config.email}</a></li>}
                {config?.whatsapp && <li className="flex items-start gap-3"><MessageCircle className="w-4 h-4 mt-1 text-primary" /><a href={`https://wa.me/${config.whatsapp.replace(/\D/g, "")}`} className="hover:text-primary">{config.whatsapp}</a></li>}
                {config?.address && <li className="flex items-start gap-3"><MapPin className="w-4 h-4 mt-1 text-primary" /><span>{config.address}</span></li>}
              </ul>
            </div>
            <div className="border border-border rounded-sm p-6 bg-secondary/30">
              <p className="text-xs uppercase tracking-[0.3em] font-bold mb-2 text-primary">Tiempo de respuesta</p>
              <p className="text-sm text-muted-foreground">Respondemos en menos de 24 horas hábiles.</p>
            </div>
          </div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="lg:col-span-2 border border-border rounded-sm p-6 lg:p-10 bg-background">
            {done ? (
              <div className="text-center py-12" data-testid="lead-success">
                <CheckCircle2 className="w-16 h-16 text-primary mx-auto mb-6" />
                <h3 className="font-display text-3xl font-black tracking-tight mb-3">Solicitud enviada</h3>
                <p className="text-muted-foreground">Te contactaremos pronto para conversar tu proyecto.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Nombre *" required value={form.name} onChange={set("name")} testid="field-name" />
                  <Field label="Empresa" value={form.company} onChange={set("company")} testid="field-company" />
                  <Field label="Cargo" value={form.role} onChange={set("role")} testid="field-role" />
                  <Field label="Email *" type="email" required value={form.email} onChange={set("email")} testid="field-email" />
                  <Field label="WhatsApp" value={form.whatsapp} onChange={set("whatsapp")} testid="field-whatsapp" />
                  <Field label="Ciudad" value={form.city} onChange={set("city")} testid="field-city" />
                  <Select label="Tipo de interés" value={form.interest} onChange={set("interest")} options={INTERESTS} testid="field-interest" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest font-bold mb-2 block">Mensaje</label>
                  <textarea value={form.message} onChange={set("message")} rows={5} data-testid="field-message" className="w-full px-4 py-3 bg-background border border-border rounded-sm focus:border-primary outline-none text-sm resize-none" />
                </div>
                <label className="flex items-start gap-3 text-sm">
                  <input type="checkbox" checked={form.accept_contact} onChange={set("accept_contact")} data-testid="field-accept" className="mt-1 accent-primary" />
                  <span className="text-muted-foreground">Acepto que INFLUCAST me contacte por email o WhatsApp.</span>
                </label>
                {error && <p className="text-sm text-destructive">{error}</p>}
                <button type="submit" disabled={sending} data-testid="submit-lead" className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-10 py-3.5 bg-primary text-primary-foreground font-bold rounded-sm hover:opacity-90 disabled:opacity-50 transition-opacity">
                  {sending ? "Enviando..." : "Enviar solicitud"}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest font-bold mb-2 block">{label}</label>
      <input data-testid={testid} {...props} className="w-full h-11 px-4 bg-background border border-border rounded-sm focus:border-primary outline-none text-sm" />
    </div>
  );
}

function Select({ label, options, testid, ...props }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest font-bold mb-2 block">{label}</label>
      <select data-testid={testid} {...props} className="w-full h-11 px-3 bg-background border border-border rounded-sm focus:border-primary outline-none text-sm">
        {options.map((o) => <option key={o}>{o}</option>)}
      </select>
    </div>
  );
}
